import React, {
  useCallback, useEffect, useId, useRef, useState,
} from 'react';
import {
  Bold, Heading, Link2, List, Trash2, UploadCloud,
} from 'lucide-react';

import {
  completeHubImageUpload, loadHubPage, prepareHubImageUpload, putProductImage,
} from './api';
import { HUB_ICONS, HubIcon } from './hubIcons';
import { tx } from './i18n';
import { XomicsSelect } from './parts';
import { ENGLISH } from './siteWords';
import { errorMessage } from './ui';

// The fields the Longevity Hub page is written with: words (one line, a few
// lines, or a text with paragraphs and lists), a photo, where something
// leads, a drawing, how a list looks and a category.

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const ROWS = { text: 2, rich: 6 };

// ---------------------------------------------------------------------- words

// Words the storefront's page files wrote keep those files' form in the
// database (<0>words</0> for what a file set in bold, or as the link back to
// the Hub in the line above a title). A person is shown the marks they would
// type themselves; the stored words change only if they change them.
export function friendly(value, field = '') {
  // In the line above a title the words before the first "›" are the link
  // back to the Hub, whatever they say, so they are shown as plain words.
  const pair = field === 'crumb' ? '$2' : '**$2**';
  return String(value ?? '').replace(/<(\d+)>(.*?)<\/\1>/g, pair).replace(/<\d+\/>/g, '');
}

// A box as tall as its words, within reason.
function rowsFor(value, least) {
  const lines = String(value).split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / 84)), 0);
  return Math.max(least, Math.min(18, lines + 1));
}

function insertAt(element, value, onChange, build) {
  const from = element.selectionStart ?? value.length;
  const to = element.selectionEnd ?? from;
  const { text, select } = build(value.slice(from, to));
  onChange(`${value.slice(0, from)}${text}${value.slice(to)}`);
  window.requestAnimationFrame(() => {
    element.focus();
    element.setSelectionRange(from + select[0], from + select[1]);
  });
}

// Every line the selection touches gets the mark in front of it.
function markLines(element, value, onChange, mark) {
  const from = value.lastIndexOf('\n', (element.selectionStart ?? 0) - 1) + 1;
  const end = value.indexOf('\n', element.selectionEnd ?? from);
  const to = end < 0 ? value.length : end;
  const lines = value.slice(from, to).split('\n').map((line) => (line.startsWith(mark) ? line : `${mark}${line}`)).join('\n');
  onChange(`${value.slice(0, from)}${lines}${value.slice(to)}`);
  window.requestAnimationFrame(() => { element.focus(); element.setSelectionRange(from, from + lines.length); });
}

// The buttons over a text: what a person would otherwise have to type as marks.
function Tools({ box, value, onChange, rich, hub, products, disabled }) {
  const [linking, setLinking] = useState(null);
  const sample = tx('ui.hub.tools.words');
  return <div className="xo-hub-tools">
    <button type="button" disabled={disabled} title={tx('ui.hub.tools.bold')} aria-label={tx('ui.hub.tools.bold')}
      onClick={() => insertAt(box.current, value, onChange, (picked) => {
        const words = picked || sample;
        return { text: `**${words}**`, select: [2, 2 + words.length] };
      })}><Bold aria-hidden="true" /></button>
    <button type="button" disabled={disabled} title={tx('ui.hub.tools.link')} aria-label={tx('ui.hub.tools.link')}
      className={linking ? 'is-on' : ''}
      onClick={() => {
        const element = box.current;
        setLinking(linking ? null : {
          from: element.selectionStart ?? value.length, to: element.selectionEnd ?? value.length,
          words: value.slice(element.selectionStart ?? 0, element.selectionEnd ?? 0), link: null,
        });
      }}><Link2 aria-hidden="true" /></button>
    {rich && <>
      <button type="button" disabled={disabled} title={tx('ui.hub.tools.list')} aria-label={tx('ui.hub.tools.list')}
        onClick={() => markLines(box.current, value, onChange, '• ')}><List aria-hidden="true" /></button>
      <button type="button" disabled={disabled} title={tx('ui.hub.tools.heading')} aria-label={tx('ui.hub.tools.heading')}
        onClick={() => markLines(box.current, value, onChange, '### ')}><Heading aria-hidden="true" /></button>
    </>}
    {linking && <div className="xo-hub-linking">
      <label>{tx('ui.hub.tools.link_words')}
        <input className="xo-field" type="text" value={linking.words} onChange={(event) => setLinking({ ...linking, words: event.target.value })} /></label>
      <LinkField label={tx('ui.hub.link.title')} value={linking.link} hub={hub} products={products} inText
        onChange={(link) => setLinking({ ...linking, link })} />
      <div className="xo-hub-linking-foot">
        <button type="button" className="xo-btn-2" onClick={() => setLinking(null)}>{tx('ui.hub.cancel')}</button>
        <button type="button" className="xo-btn" disabled={!linking.words.trim() || !linking.link}
          onClick={() => {
            const mark = `[${linking.words.trim()}](${linking.link})`;
            onChange(`${value.slice(0, linking.from)}${mark}${value.slice(linking.to)}`);
            setLinking(null);
          }}>{tx('ui.hub.tools.link_add')}</button>
      </div>
    </div>}
  </div>;
}

// One field of words. `text` is the stored text (its English row and, for
// another language, that language's row beside it); `value` is what stands in
// the box now. `fallback`: the words the store shows while this has none.
export function WordField({
  kind, field = '', label, text, language, value, onChange, onFocus, disabled, fallback = '', hub, products,
}) {
  const id = useId();
  const box = useRef(null);
  const english = friendly(text.value, field);
  const other = language !== ENGLISH;
  // Another language is written over English words: without them there is nothing to translate.
  const waits = other && english === '' && fallback === '';
  const locked = disabled || waits;
  const shared = {
    id, ref: box, value, disabled: locked, dir: 'auto',
    placeholder: other ? english || fallback : fallback,
    onChange: (event) => onChange(event.target.value),
    onFocus,
  };
  return <div className={`xo-hub-field is-${kind}`}>
    <label htmlFor={id}>{label}</label>
    {kind !== 'line' && <Tools box={box} value={value} onChange={onChange} rich={kind === 'rich'}
      hub={hub} products={products} disabled={locked} />}
    {kind === 'line'
      ? <input className="xo-field" type="text" {...shared} />
      : <textarea className="xo-field" rows={rowsFor(value || shared.placeholder, ROWS[kind])} {...shared} />}
    {waits && <p className="xo-hub-field-note">{tx('ui.hub.words.english_first')}</p>}
    {other && english !== '' && <p className="xo-hub-field-english" dir="auto"><b>{tx('ui.hub.words.english')}</b> {english}</p>}
  </div>;
}

// ---------------------------------------------------------------------- photo

async function sha256Of(file) {
  const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// A photo file handed to XOmics the way a product photo is: the browser puts
// it straight into storage, then XOmics checks it and makes its sizes.
// Answers with the photo as the Hub keeps it ({ id, url, ... }).
export async function uploadHubPhoto(file, onProgress = () => {}) {
  if (!IMAGE_TYPES.has(file.type) || file.size < 32 || file.size > MAX_IMAGE_BYTES) {
    throw new Error(tx('ui.hub.photo.bad'));
  }
  onProgress(tx('ui.hub.photo.preparing'));
  const declaration = { contentType: file.type, byteSize: file.size, sha256: await sha256Of(file) };
  const prepared = await prepareHubImageUpload(declaration);
  if (!prepared.alreadyUploaded) {
    onProgress(tx('ui.hub.photo.uploading'));
    await putProductImage(prepared.putUrl, file);
  }
  onProgress(tx('ui.hub.photo.checking'));
  return completeHubImageUpload({ ...declaration, objectKey: prepared.objectKey });
}

// A section's or a card's photo: dropped or chosen, changed, taken off; and
// where the store crops it (`canFocus`), the part that must stay in view,
// chosen by clicking it. `onSet(changes)` saves { imageId } or { focus }.
export function PhotoField({
  photo, canFocus = false, disabled, onSet, label = tx('ui.hub.photo.title'),
}) {
  const input = useRef(null);
  const [busy, setBusy] = useState('');
  const [failed, setFailed] = useState('');
  const [dragging, setDragging] = useState(false);

  const take = useCallback(async (files) => {
    const file = files?.[0];
    if (!file) return;
    setFailed('');
    try {
      const image = await uploadHubPhoto(file, setBusy);
      await onSet({ imageId: image.id });
    } catch (error) {
      setFailed(errorMessage(error));
    } finally {
      setBusy('');
      if (input.current) input.current.value = '';
    }
  }, [onSet]);

  const focus = photo?.focus || [50, 50];
  return <div className="xo-hub-photo">
    <span className="xo-hub-label">{label}</span>
    {photo ? <div className="xo-hub-photo-has">
      <button type="button" className={`xo-hub-photo-view${canFocus ? ' is-focus' : ''}`} disabled={disabled || !canFocus || busy !== ''}
        aria-label={canFocus ? tx('ui.hub.photo.focus') : label} title={canFocus ? tx('ui.hub.photo.focus') : undefined}
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const at = (value, size) => Math.max(0, Math.min(100, Math.round((value / size) * 100)));
          onSet({ focus: [at(event.clientX - box.left, box.width), at(event.clientY - box.top, box.height)] });
        }}>
        <img src={photo.thumbnailUrl || photo.url} alt="" />
        {canFocus && <i aria-hidden="true" style={{ left: `${focus[0]}%`, top: `${focus[1]}%` }} />}
      </button>
      <div className="xo-hub-photo-side">
        <button type="button" className="xo-btn-2" disabled={disabled || busy !== ''} onClick={() => input.current?.click()}>
          <UploadCloud aria-hidden="true" />{busy || tx('ui.hub.photo.change')}</button>
        <button type="button" className="xo-btn-2" disabled={disabled || busy !== ''} onClick={() => onSet({ imageId: null })}>
          <Trash2 aria-hidden="true" />{tx('ui.hub.photo.remove')}</button>
      </div>
    </div> : <button type="button" className={`xo-hub-drop${dragging ? ' is-dragging' : ''}`} disabled={disabled || busy !== ''}
      onClick={() => input.current?.click()}
      onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
      onDrop={(event) => { event.preventDefault(); setDragging(false); take(event.dataTransfer.files); }}>
      <UploadCloud aria-hidden="true" />
      <span><strong>{busy || tx('ui.hub.photo.drop')}</strong><small>{tx('ui.hub.photo.choose')}</small></span>
    </button>}
    <input ref={input} className="xo-visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,image/avif"
      disabled={disabled} aria-label={label} onChange={(event) => take(event.target.files)} />
    {failed && <p className="xomics-field-error" role="alert">{failed}</p>}
  </div>;
}

// ----------------------------------------------------------------------- link

const SEARCH = /^\/hub\/search\/\?q=(.*)$/;

// A link as its parts: which kind it is and what it names.
export function readLink(value) {
  if (!value) return { mode: 'none' };
  const page = /^page:(\d+)(?:#(.*))?$/.exec(value);
  if (page) return { mode: 'page', pageId: Number(page[1]), part: page[2] || '' };
  const product = /^product:(\d+)$/.exec(value);
  if (product) return { mode: 'product', productId: Number(product[1]) };
  const search = SEARCH.exec(value);
  if (search) return { mode: 'search', words: decodeURIComponent(search[1].replace(/\+/g, ' ')) };
  return { mode: 'address', address: value };
}

const validAddress = (value) => /^(?:\/|https:\/\/)\S+$/.test(value);

// Where a button, a card or a link in a text leads: a Hub page (and, if
// wanted, one section of it), a product, a search in the Hub, or any other
// address. A person picks; nobody types an id. `inText`: a link inside a
// text, which is kept as an address, so it names the page's address as it
// is now. `onChange(value)` is called with a whole link, or null for nowhere.
export function LinkField({
  label, value, onChange, hub, products, disabled, inText = false,
}) {
  const id = useId();
  const link = readLink(inText ? null : value);
  const [mode, setMode] = useState(inText ? 'page' : link.mode);
  const [typed, setTyped] = useState(link.mode === 'search' ? link.words : link.mode === 'address' ? link.address : '');
  const [picked, setPicked] = useState(null);
  const [parts, setParts] = useState([]);
  useEffect(() => {
    if (inText) return;
    const next = readLink(value);
    setMode(next.mode);
    setTyped(next.mode === 'search' ? next.words : next.mode === 'address' ? next.address : '');
  }, [inText, value]);

  const pages = [...(hub.front ? [{ id: hub.front.id, path: hub.front.path, name: tx('ui.hub.side.front'), isPublished: true }] : []),
    ...hub.pages.map((page) => ({ id: page.id, path: page.path, name: page.title.value || page.path, isPublished: page.isPublished }))];
  const pageId = inText ? picked?.pageId ?? null : link.mode === 'page' ? link.pageId : null;
  const part = inText ? picked?.part ?? '' : link.mode === 'page' ? link.part : '';
  // The sections of the chosen page a reader can be sent to.
  useEffect(() => {
    let live = true;
    setParts([]);
    if (mode !== 'page' || !pageId) return undefined;
    loadHubPage(pageId, ENGLISH).then(({ page }) => {
      if (!live) return;
      setParts(page.sections.filter((section) => section.anchor && section.isActive)
        .map((section) => ({ anchor: section.anchor, name: section.words.heading?.value || section.words.short?.value || section.anchor })));
    }).catch(() => {});
    return () => { live = false; };
  }, [mode, pageId]);

  const sayPage = (nextId, nextPart) => {
    const page = pages.find((entry) => entry.id === nextId);
    if (!page) return;
    if (inText) {
      setPicked({ pageId: nextId, part: nextPart });
      onChange(`${page.path}${nextPart ? `#${nextPart}` : ''}`);
    } else onChange(`page:${nextId}${nextPart ? `#${nextPart}` : ''}`);
  };
  const sayProduct = (productId) => {
    const product = products.find((entry) => entry.id === productId);
    if (!product) return;
    onChange(inText ? product.path : `product:${productId}`);
  };
  const sayTyped = () => {
    const words = typed.trim();
    if (mode === 'search') onChange(words ? `/hub/search/?q=${encodeURIComponent(words)}` : null);
    else if (mode === 'address') onChange(validAddress(words) ? words : null);
  };
  const chosenPage = pages.find((entry) => entry.id === pageId);
  const sold = products.filter((product) => product.path);

  return <div className="xo-hub-link">
    <label className="xo-hub-label" htmlFor={id}>{label}</label>
    <div className="xo-hub-link-row">
      <XomicsSelect id={id} value={mode} disabled={disabled} onChange={(event) => {
        setMode(event.target.value);
        if (event.target.value === 'none') onChange(null);
      }}>
        <option value="page">{tx('ui.hub.link.page')}</option>
        <option value="product">{tx('ui.hub.link.product')}</option>
        <option value="search">{tx('ui.hub.link.search')}</option>
        <option value="address">{tx('ui.hub.link.address')}</option>
        {!inText && <option value="none">{tx('ui.hub.link.none')}</option>}
      </XomicsSelect>
      {mode === 'page' && <XomicsSelect value={pageId ?? ''} disabled={disabled} aria-label={tx('ui.hub.link.choose_page')}
        onChange={(event) => sayPage(Number(event.target.value), '')}>
        <option value="">{tx('ui.hub.link.choose_page')}</option>
        {pages.map((page) => <option key={page.id} value={page.id}>{page.isPublished ? page.name : `${page.name} (${tx('ui.hub.side.draft')})`}</option>)}
      </XomicsSelect>}
      {mode === 'product' && <XomicsSelect value={inText ? '' : link.productId ?? ''} disabled={disabled}
        aria-label={tx('ui.hub.link.choose_product')} onChange={(event) => sayProduct(Number(event.target.value))}>
        <option value="">{tx('ui.hub.link.choose_product')}</option>
        {sold.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
      </XomicsSelect>}
      {(mode === 'search' || mode === 'address') && <input className="xo-field" type="text" value={typed} disabled={disabled}
        aria-label={mode === 'search' ? tx('ui.hub.link.search_words') : tx('ui.hub.link.address_hint')}
        placeholder={mode === 'search' ? tx('ui.hub.link.search_words') : '/testing/ · https://…'}
        onChange={(event) => setTyped(event.target.value)} onBlur={sayTyped}
        onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); sayTyped(); } }} />}
    </div>
    {mode === 'page' && chosenPage && parts.length > 0 && <div className="xo-hub-link-row is-part">
      <span>{tx('ui.hub.link.part')}</span>
      <XomicsSelect value={part} disabled={disabled} aria-label={tx('ui.hub.link.part')}
        onChange={(event) => sayPage(chosenPage.id, event.target.value)}>
        <option value="">{tx('ui.hub.link.whole')}</option>
        {parts.map((entry) => <option key={entry.anchor} value={entry.anchor}>{friendly(entry.name)}</option>)}
      </XomicsSelect>
    </div>}
    {mode === 'page' && chosenPage && !chosenPage.isPublished && <p className="xo-hub-field-note">{tx('ui.hub.link.draft_note')}</p>}
    {mode === 'address' && typed.trim() !== '' && !validAddress(typed.trim())
      && <p className="xo-hub-field-note">{tx('ui.hub.link.address_hint')}</p>}
  </div>;
}

// ------------------------------------------------------- drawing, look, category

export function IconField({ value, onChange, disabled }) {
  return <div className="xo-hub-icons" role="radiogroup" aria-label={tx('ui.hub.icon.title')}>
    {Object.keys(HUB_ICONS).map((name) => <button key={name} type="button" role="radio" aria-checked={value === name}
      aria-label={name} title={name} disabled={disabled} className={value === name ? 'is-on' : ''}
      onClick={() => onChange(name)}><HubIcon name={name} /></button>)}
  </div>;
}

// How a text section's points look, each shown as a small sketch of itself.
const LOOKS = Object.freeze({
  dots: <><circle cx="6" cy="8" r="1.6" /><path d="M11 8h22" /><circle cx="6" cy="16" r="1.6" /><path d="M11 16h18" /><circle cx="6" cy="24" r="1.6" /><path d="M11 24h20" /></>,
  tiles: <><rect x="3" y="4" width="15" height="11" rx="2.5" /><rect x="22" y="4" width="15" height="11" rx="2.5" /><rect x="3" y="18" width="15" height="11" rx="2.5" /><rect x="22" y="18" width="15" height="11" rx="2.5" /></>,
  rows: <><rect x="3" y="4" width="7" height="7" rx="2" /><path d="M14 7.5h22" /><rect x="3" y="13" width="7" height="7" rx="2" /><path d="M14 16.5h18" /><rect x="3" y="22" width="7" height="7" rx="2" /><path d="M14 25.5h20" /></>,
  steps: <><text x="3" y="11">1</text><path d="M11 8h24" /><text x="3" y="19">2</text><path d="M11 16h20" /><text x="3" y="27">3</text><path d="M11 24h22" /></>,
});

export function LookField({ value, looks, onChange, disabled }) {
  return <div className="xo-hub-looks" role="radiogroup" aria-label={tx('ui.hub.look.title')}>
    <span className="xo-hub-label">{tx('ui.hub.look.title')}</span>
    <div>
      {looks.map((look) => <button key={look} type="button" role="radio" aria-checked={value === look} disabled={disabled}
        className={value === look ? 'is-on' : ''} onClick={() => onChange(look)}>
        <svg viewBox="0 0 40 32" aria-hidden="true">{LOOKS[look]}</svg>
        <span>{tx(`ui.hub.look.${look}`)}</span>
      </button>)}
    </div>
  </div>;
}

export function CategoryField({ value, categories, onChange, disabled, label = tx('ui.hub.category.title') }) {
  const id = useId();
  return <div className="xo-hub-link">
    <label className="xo-hub-label" htmlFor={id}>{label}</label>
    <XomicsSelect id={id} value={value ?? ''} disabled={disabled}
      onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}>
      <option value="">{tx('ui.hub.category.none')}</option>
      {categories.map((category) => <option key={category.id} value={category.id}>{category.name.value}</option>)}
    </XomicsSelect>
  </div>;
}
